import { useCallback, useContext, useEffect, useState } from 'react'
import semver from 'semver'
import { BrowserRouter as Router } from 'react-router-dom'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { GetDataError, RestfulProvider } from 'restful-react'
import {
    AppEvents,
    ModelUpdateEventType,
    UserContext,
    UserContextType,
    UserProfile,
    createUserProfileFromApiUserDocument,
} from './UserContext'
import { logger, LOG_LEVEL_TABEL } from './services/Logger'
import AuthenticatedApp from './AuthenticatedApp'
import UnauthenticatedApp from './UnauthenticatedApp'
import { UserContextProvider } from './UserContext'
import Api from './services/Api'
import { AppContext, AppContextProvider, AppContextType, ExternalEvent } from './AppContext'
import LoginError from './pages/ErrorPages/LoginError'
import InternetConnectionError from './pages/ErrorPages/InternetConnectionError'
import RootSnackbarController from './pages/RootSnackbarController'
import { env } from './services/EnvironmentVariables'
import moment from 'moment'
import momentDurationFormatSetup from 'moment-duration-format'
import MainWindowTitleBar from './MainWindowTitleBar'
import * as Constants from './services/Constants'
import * as EnvironmentVariables from './services/EnvironmentVariables'
import IsOfflineBanner from './components/IsOfflineBanner'
import usePersistentWindowMetrics from './services/usePersistentWindowMetrics'
import useRootSnackbar from './services/useRootSnackbar'
import 'react-loader-spinner/dist/loader/css/react-spinner-loader.css'
import './transitions.css'
import { FeatureFlags } from './services/FeatureFlags'
import { canaryFeatures } from './services/CanaryFeatures'
import { persistent } from './services/LocalStorage'
import { sfuNodeSelection } from './services/SfuNodeSelection'
import { getAuthTokenFromLocalUserCache } from './Logic'
import ElectronIPC from './services/ElectronIPC'
import * as Sentry from '@sentry/react'
import useMaintenanceMessageService from './services/useMaintenanceMessageService'
import MaintenanceError from './pages/ErrorPages/MaintenanceError'
import clone from 'clone'
import PromptDialogManager from './pages/PromptDialogManager'
import useExternalEventLoaderService from './services/useExternalEventLoaderService'
import ShowRestartRequiredDialog from './dialogs/ShowRestartRequiredDialog'
import { wsConnection } from './services/WSConnectionInstance'
import { v4 as uuidv4 } from 'uuid'
import { getProcessArchitecture, stripSemverVersion } from './utils'
import path from 'path'
import useDeveloperOptionsService from './functionalities/developer-options/useDeveloperOptionsService'
import ConsoleLogOverride from './actions/ConsoleLogOverride'
import { getAppSerialId } from './actions/AppSerialId'
import BrowserNotSupportedError from './pages/ErrorPages/BrowserNotSupportedError'
import { useI18nLanguageActions } from './functionalities/Internationalization/useI18nLanguageActions'
import { StyleChangedEventEmitter } from './StyleChangedEventEmitter'
import 'react-datepicker/dist/react-datepicker.css'
import useLogout from './app-services/useLogout'
import { serializeError } from 'serialize-error'
import { RoomContext, RoomContextProvider, RoomContextState, RoomContextType } from './RoomContext'
import { getAudioEngineInstance } from './core/AudioEngine'
import Loader from 'react-loader-spinner'
import { setAnalyticsIdentity } from './services/AnalyticsService'
import useDeveloperOptionsActions from './functionalities/developer-options/useDeveloperOptionsActions'
import { useTranslation } from 'react-i18next'
import useRoomActions from './services/useRoomActions'
import { ExternalEventEmitter } from './ExternalEventEmitter'
import useMusePluginStateService from './components/useMusePluginStateService'
import { Tooltip } from 'radix-ui'
import { useNonIdempotentEffect } from './hooks/useNonIdempotentEffect'

const log = logger.createChildLogger({ name: 'App' })

function FallbackComponent({ error, componentStack, resetError }: any) {
    // resetError()
    return (
        <div style={{ color: '#f1f1f1' }}>
            <div>An error has ocurred: {error.toString()}</div>
            <div style={{ marginTop: 10, fontSize: '80%' }}>{componentStack}</div>
        </div>
    )
}

function registerSentry(noTelemetry: boolean, userUrn: string, profile: UserProfile, eventDropList: string[]) {
    try {
        if (noTelemetry) {
            Sentry.close(0)
            return
        }

        Sentry.setUser({
            userUrn: userUrn,
            username: profile.username,
            email: profile.email,
        })

        // compile into a list of regexes
        const sentryEventSkipList = eventDropList.map((s) => new RegExp(s))

        Sentry.addEventProcessor((event, hint) => {
            try {
                // filter errors matching `sentryEventSkipList` (notice the early return)
                const error = hint.originalException
                if (error instanceof Error && typeof error.message === 'string') {
                    const msg = error.message
                    for (const regex of sentryEventSkipList) {
                        if (regex.test(msg)) {
                            return null // Drop this error
                        }
                    }
                }
            } catch (e) {
                log.error('registerSentry', 'an exception occurred while filtering Sentry error at event processor.', {
                    e,
                })
            }
            return event
        })

        // TODO: On logout: `Sentry.configureScope(scope => scope.setUser(null))`
    } catch (e) {
        log.error('registerSentry', 'an exception occurred while registering sentry!', { e })
    }
}

function App() {
    useEffect(() => {
        ////
        //// on styles in <head> changed, emit the styles so that Electron modals
        //// can copy it. for now, we send all the styles wholesale.
        ////
        const observer = new MutationObserver((mutationsList) => {
            // copy the style tags
            let styleContent = ''
            const styleTags = document.head.getElementsByTagName('style')
            for (let tag of styleTags) {
                styleContent += tag.textContent + '\n'
            }

            // copy the link stylesheets
            Array.from(document.querySelectorAll('link[rel="stylesheet"]')).forEach((link: any) => {
                try {
                    if (!link.sheet) {
                        return
                    }

                    const stylesheetContent = Array.from(link.sheet.cssRules)
                        .map((rule: any) => rule.cssText)
                        .join('\n')
                    styleContent += stylesheetContent
                } catch (e) {
                    console.error(e)
                }
            })

            // advertise this change.
            StyleChangedEventEmitter.changed(styleContent)
        })

        observer.observe(document.head, { childList: true, subtree: true })
        return () => {
            observer.disconnect()
        }
    }, [])

    return (
        <Sentry.ErrorBoundary fallback={FallbackComponent} showDialog>
            <DndProvider backend={HTML5Backend}>
                <Tooltip.Provider delayDuration={400}>
                    <Router>
                        <AppContextProvider>
                            <RootSnackbarController />
                            <Authentication />
                            <PromptDialogManager />
                        </AppContextProvider>
                    </Router>
                </Tooltip.Provider>
            </DndProvider>
        </Sentry.ErrorBoundary>
    )
}

function Authentication() {
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const { t } = useTranslation()

    useExternalEventLoaderService()

    useMusePluginStateService()

    useDeveloperOptionsService()

    const { setOptionsFromEnv } = useDeveloperOptionsActions()

    const { detectSystemLanguage, getPreferredLanguage, setPreferredLanguage } = useI18nLanguageActions()

    const RootSnackbar = useRootSnackbar()

    const [isLoaded, set_isLoaded] = useState(false)

    usePersistentWindowMetrics({
        key: 'StartupPage.winmetrics',
        appliedLocations: ['/', '/login', '/register'],
    })

    useEffect(() => {
        const init = async () => {
            /* @ts-ignore */
            momentDurationFormatSetup(moment)

            // load app environment variables. if browser session, then loads dummy environment
            // variables via `setBrowserSessionEnvironmentVariables()`.
            const env = await EnvironmentVariables.load()

            // set app constants via `env`.
            Constants.loadFromEnv(env)
            set_isLoaded(true)

            if (!appState.appSettings.developerOptions.saveConfiguration) {
                setOptionsFromEnv(env)
            }

            if (env.LOG_LEVEL < LOG_LEVEL_TABEL['info']) {
                // console.log is considered info, so disable console.log
                // when LOG_LEVEL is not info
                ConsoleLogOverride.disableConsoleLog()
            }

            // set preferred language
            const systemLanguage = detectSystemLanguage()
            const preferredLanguage = getPreferredLanguage()
            console.log(`init: got preferredLanguage="${preferredLanguage}".`)
            setPreferredLanguage(preferredLanguage)

            // Hard-code enable advanced remote recording for darwin
            if (env.OS === 'darwin') {
                FeatureFlags.enable('advanced-remote-recording')
                FeatureFlags.enable('simple-remote-recording')
            } else {
                FeatureFlags.disable('advanced-remote-recording')
                FeatureFlags.enable('simple-remote-recording')
            }

            // if is web app, then enable browser modals, not otherwise.
            FeatureFlags.set('browser-modals', !env.IS_ELECTRON)

            // enable desktop-app-only features if IS_ELECTRON, not otherwise.
            FeatureFlags.set('desktop-app-only-feature', env.IS_ELECTRON)

            FeatureFlags.set(
                'browser-default-speaker-output-only',
                env.BROWSER_NAME === 'safari' || env.BROWSER_NAME === 'ios'
            )

            // Disable 'obs-screen-share' by default
            FeatureFlags.disable('obs-screen-share')

            // disable back button event on mouse
            window.addEventListener('mouseup', (e) => {
                if (e.button === 3 || e.button === 4) e.preventDefault()
            })

            // force set `appState.appSettings.screenShareDesktopVideoOptions` defaults once regardless
            // of what the users' previous settings were.
            {
                const b = persistent.getBoolean('screenShareDesktopVideoOptions1.5.7DefaultsApplied')
                if (!b) {
                    appDispatch({
                        type: 'update_app_settings',
                        value: { screenShareDesktopVideoOptions: { optionHeight: 720, optionFramerate: 20 } },
                    })
                    persistent.set('screenShareDesktopVideoOptions1.5.7DefaultsApplied', true)
                }
            }

            // check cpu arch compatiblity for osx
            ;(async () => {
                if (env.OS === 'darwin') {
                    const processArch = await getProcessArchitecture()

                    const isArm = !!(processArch || '').match('arm')
                    log.info(
                        'checkCpuCompatibility',
                        `processArch=${processArch} isArm=${isArm} env.APP_ARCH=${env.APP_ARCH}`
                    )

                    if (isArm && env.APP_ARCH === 'x64') {
                        log.verbose('checkCpuCompatibility', `showing dialog "app_cpu_arch_mismatch".`)
                        appDispatch({
                            type: 'new_message_dialog',
                            value: {
                                id: 'app_cpu_arch_mismatch',
                                text:
                                    'It looks like your computer is an Apple Silicon (M1, M2, etc) device but the ' +
                                    'Muse app you are running is built for Intel.\n' +
                                    'For a more performant experience, please download Muse built for Apple Silicon at ' +
                                    'https://www.musesessions.co/download.',
                                negativeButtonDoNotShowAgain: true,
                            },
                        })
                    }
                }
            })()

            // handle upgrade from v2 to v3
            {
                try {
                    const lastOpenedVersion = persistent.getString('lastOpenedVersion')
                    if (lastOpenedVersion) {
                        const lastOpenedVersionStripped = stripSemverVersion(lastOpenedVersion)
                        if (lastOpenedVersionStripped && semver.lt(lastOpenedVersionStripped, '3.0.0')) {
                            persistent.delete('CollaboratePage.winmetrics.normal')
                            persistent.delete('CollaboratePage.winmetrics.retracted')
                            persistent.delete('StartupPage.winmetrics')
                            log.info('init', 'cleared winmetrics for upgrading from v2.')
                        }
                    }
                } catch (e) {}
            }

            log.checkpoint('init_complete', '', {
                systemLanguage,
                preferredLanguage,
            })
        }
        init()
    }, [])

    useEffect(() => {
        const handler = (externalEvent: ExternalEvent) => {
            if (externalEvent.eventName === 'main-thread-error') {
                RootSnackbar.show({
                    severity: 'error',
                    message: externalEvent.value,
                })
            }
        }

        ExternalEventEmitter.on('event', handler)

        return () => {
            ExternalEventEmitter.removeListener('event', handler)
        }
    }, [])

    const onRequest = useCallback((req: Request) => {
        const storedAuthToken = getAuthTokenFromLocalUserCache()
        if (storedAuthToken) {
            req.headers.append('Authorization', 'Bearer ' + storedAuthToken)
        }

        req.headers.append('X-Client-Version', env.APP_VERSION)
    }, [])

    // Must not render anything as the environment variables are loading because
    // the components below depend on it.
    if (!isLoaded) {
        return null
    }

    if (!env.IS_ELECTRON && !Constants.supported_browsers.includes(env.BROWSER_NAME)) {
        return <BrowserNotSupportedError browserName={env.BROWSER_NAME} />
    }

    return (
        <>
            {Constants.has_custom_title_bar && <MainWindowTitleBar />}
            {/* @ts-ignore restful-react does not officially support react 18 */}
            <RestfulProvider base={Constants.api_base_path} onRequest={onRequest}>
                <UserContextProvider>
                    <RoomContextProvider>
                        <LoginRouter />
                    </RoomContextProvider>
                </UserContextProvider>
            </RestfulProvider>
            <ShowRestartRequiredDialog />
            <IsOfflineBanner />
        </>
    )
}

/**
 * This component takes in an authToken, fetches user information using the authToken
 * and the Muse API, then stores the user information in UserContext.
 */
function LoginRouter() {
    const { state: appState, dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType
    const { state, dispatch } = useContext(UserContext) as UserContextType

    const { boostRoomToPro } = useRoomActions()

    const [error, set_error] = useState<any>(null)

    const RootSnackbar = useRootSnackbar()

    const { logout } = useLogout()

    const handleWebsocketMessages = async (json: any) => {
        try {
            if (json.type === 'new-app-event') {
                const ev: AppEvents = json.data
                dispatch({
                    type: 'append_app_events',
                    value: ev,
                })
            } else if (json.type === 'model-update-event') {
                const ev = json.data as ModelUpdateEventType

                console.log(`rtcSession.onModelUpdateEvent: ev=%O`, ev)
                if (ev.modelName === 'User') {
                    const next: any = {}
                    if (ev.updatedFields?.emailVerified != null) {
                        next['emailVerified'] = ev.updatedFields?.emailVerified
                    }
                    if (ev.updatedFields?.tier === 'pro') {
                        log.checkpoint('realtime_tier_change', JSON.stringify(ev))
                        const roomTierPolicy = await boostRoomToPro()
                        if (roomTierPolicy) {
                            next['policy'] = {
                                ...state.profile?.policy,
                                roomTierPolicy,
                            }
                        }
                    }
                    dispatch({
                        type: 'update_user_profile',
                        value: next,
                    })
                } else if (ev.modelName === 'Room') {
                    const next: Partial<RoomContextState> = {}
                    if (Array.isArray(ev.updatedFields?.subrooms)) {
                        next.subrooms = ev.updatedFields?.subrooms
                    }
                    if (typeof ev.updatedFields?.isSubroomsEnabled === 'boolean') {
                        next.isSubroomsEnabled = ev.updatedFields?.isSubroomsEnabled
                    }
                    if (typeof ev.updatedFields?.isListeningSession === 'boolean') {
                        next.isListeningSession = ev.updatedFields?.isListeningSession
                    }

                    if (Object.keys(next).length > 0) {
                        roomDispatch({
                            type: 'update_room_context',
                            value: next,
                        })
                    }
                }
            }
        } catch (e) {
            console.error(e)
        }
    }

    const loadUser = useCallback((user: any, authToken: string) => {
        console.log(`userContext dispatch login:\nuser=${user} authToken=${authToken}`)
        if (!user || !authToken) {
            return
        }

        const userUrn = user._id
        const profile: UserProfile = createUserProfileFromApiUserDocument(user)

        if (env.TEST_USER_POLICY_NO_ALLOW_AUDIOENGINE_TX) {
            // stub
        }

        dispatch({
            type: 'login',
            value: {
                authToken: authToken,
                userUrn: userUrn,
                displayName: user.displayName,
                profile: profile,
                communityProfile: user.profile,
                isGuest: user.isGuest,
            },
        })

        // Only really needs to be called when user changes username or email.
        // This ensures that we can effectively make this retroactive.
        setAnalyticsIdentity(userUrn, profile)

        Constants.loadRuntimeConstants(user._appRuntimeConstants || {})
        console.log(
            `Got _appRuntimeConstants=${JSON.stringify(user._appRuntimeConstants, null, '  ')} from the server.`
        )

        registerSentry(env.NO_TELEMETRY, userUrn, profile, Constants.data.sentry_event_drop_list)

        appDispatch({
            type: 'update_app_settings',
            value: {
                canaryFeatures: user.canaryFeatures,
            },
        })
        canaryFeatures.update(user.canaryFeatures)

        let appSerialId = getAppSerialId()

        let sessionId = state.sessionId ? state.sessionId : uuidv4()

        dispatch({
            type: 'set_app_ids',
            value: {
                appSerialId,
                sessionId,
            },
        })

        const sfuNodes = user._appRuntimeConstants.data.sfu_nodes || []
        log.checkpoint('sfu_set_avaialble_nodes', '', { sfuNodes })
        sfuNodeSelection.setAvailableNodes(sfuNodes || [])

        // handle legacy/deprecated app settings (appState.appSettings)
        ;(async () => {
            var next = clone({ ...appState.appSettings, canaryFeatures: user.canaryFeatures })
            let changed = false
            // if `alwaysShareMuseDAWBridge` is on, set it off.
            if (next.alwaysShareMuseDAWBridge == true) {
                next.alwaysShareMuseDAWBridge = false
                changed = true
            }

            // set default `screenRecordSaveDirectory` if not set.
            if (!next.screenRecordSaveDirectory) {
                let path = Constants.defaultDirectories.screen_recording
                next.screenRecordSaveDirectory = path
                console.log(`init: set appSettings.screenRecordSaveDirectory="${path} because current one is null."`)
                ElectronIPC.checkDirectory(path, { checkWritable: true, createIfNotExists: true }).then((result) => {
                    if (!result.success) {
                        console.error(`init: failed to create screenRecordSaveDirectory="${path}".`)
                    }
                })
                changed = true
            }

            // set default `remoteRecordSaveDirectory` if not set.
            if (!next.remoteRecordSaveDirectory) {
                let path = Constants.defaultDirectories.remote_recording
                next.remoteRecordSaveDirectory = path
                console.log(`init: set appSettings.remoteRecordSaveDirectory="${path} because current one is null."`)
                ElectronIPC.checkDirectory(path, { checkWritable: true, createIfNotExists: true }).then((result) => {
                    if (!result.success) {
                        console.error(`init: failed to create remoteRecordSaveDirectory="${path}".`)
                    }
                })
                changed = true
            }

            if (env.OS === 'win32' && next.remoteRecordingIsAdvancedMode) {
                next.remoteRecordingIsAdvancedMode = false
                changed = true
            }

            if (
                !next.remoteRecordingIsAdvancedMode &&
                !next.remoteRecordingIsSimpleMode &&
                !next.recorderSaveRemoteRecording
            ) {
                next.recorderSaveRemoteRecording = true
                changed = true
            }

            // set `showWindowsDuringScreenshare` (window content protection) to true always
            // for Mac. Electron upgrade to v19 fixed the screen share performance issues, but
            // setContentProtection does not work for Mac anymore.
            if (env.OS === 'darwin') {
                if (!next.showWindowsDuringScreenshare) {
                    next.showWindowsDuringScreenshare = true
                    changed = true
                }
            }

            // reset the "Enable MIDI" button to the OFF state for Windows on app startup.
            // this is due to MIDI access issues that arise if Muse calls `navigator.requestMIDIAccess`
            // before the DAW accesses MIDI.
            if (env.OS === 'win32') {
                next.forceAccessMidiForWindows = false
                changed = true
            }

            if (env.TEST_INVALIDATE_SAVED_SCREEN_RECORDING_ID) {
                const obj = persistent.getObject('appContext.appSettings')
                if (obj) {
                    obj.screenRecordDesktop = {
                        id: Math.random().toString(),
                        label: Math.random().toString(),
                    }
                    persistent.set('appContext.appSettings', obj)
                }
            }

            // manage the audio engine log directory
            ;(() => {
                if (!next.audioEngineDevSettings.logOutputDirectory) {
                    next.audioEngineDevSettings.logOutputDirectory = path.join(env.LOGDIR || '', 'audioengine', 'logs')
                    if (env.OS === 'win32') {
                        next.audioEngineDevSettings.logOutputDirectory =
                            next.audioEngineDevSettings.logOutputDirectory.replaceAll('/', '\\')
                    }
                    changed = true
                }
                ElectronIPC.checkDirectory(next.audioEngineDevSettings.logOutputDirectory, {
                    checkWritable: true,
                    createIfNotExists: true,
                }).then((result) => {
                    if (!result.success) {
                        console.error(
                            `init: failed to create audioEngineDevSettings.logOutputDirectory="` +
                                `${next.audioEngineDevSettings.logOutputDirectory}".`
                        )
                        return
                    }
                })

                if (!next.audioEngineDevSettings.textLogFileName) {
                    next.audioEngineDevSettings.textLogFileName = 'audioengine.log'
                    changed = true
                }

                if (next.audioEngineDevSettings.tagUsername !== user.username) {
                    next.audioEngineDevSettings.tagUsername = user.username
                    changed = true
                }

                if (next.audioEngineDevSettings.tagMuseappver !== env.APP_VERSION) {
                    next.audioEngineDevSettings.tagMuseappver = env.APP_VERSION
                    changed = true
                }
            })()

            // manage the "lastOpenedVersion" localStorage variable; this variable lets us know
            // whether this app open is the first app open of a new version update.
            const lastOpenedVersion = persistent.getString('lastOpenedVersion')
            if (lastOpenedVersion && lastOpenedVersion !== env.APP_VERSION) {
                const lastOpenedVersionStripped = stripSemverVersion(lastOpenedVersion)

                // the app opened for the first time since the last update.
                console.log(`init: lastOpenedVersion=${lastOpenedVersion} !== env.APP_VERSION=${env.APP_VERSION}.`)

                // On first app-open upon installation of version 1.5.1, set remote recording simple-mode to true.
                if (env.APP_VERSION === '1.5.1') {
                    console.log(`init: setting remoteRecordingIsSimpleMode to true.`)
                    next.remoteRecordingIsSimpleMode = true
                    next.remoteRecordingIsAdvancedMode = false
                    changed = true
                }

                if (env.APP_VERSION === '1.5.9') {
                    const wm = persistent.getObject('StartupPage.winmetrics')
                    if (wm) {
                        wm.width = 810
                        persistent.set('StartupPage.winmetrics', wm)
                        ElectronIPC.setWindowBounds(wm)
                    }
                }

                // if the user is upgrading from 2.0.3, force unset using the audio engine.
                if (lastOpenedVersion === '2.0.3') {
                    persistent.getKeys().forEach((k) => {
                        try {
                            if (typeof k === 'string' && k.startsWith('AudioSettingsStore::')) {
                                let s = persistent.getObject(k)
                                if (s?.isAudioEngine) {
                                    persistent.delete(k)
                                }
                            }
                        } catch (e) {}
                    })
                }

                // enforce channel policy e.g., if chgrp==[1,2] and [1] already exists, then remove [1].
                ;(() => {
                    try {
                        persistent
                            .getKeys()
                            .filter((k) => k.startsWith('AudioSettingsStore:'))
                            .forEach((k) => {
                                const settings = persistent.getObject(k)
                                if (!settings) {
                                    return
                                }

                                const lenGte2ChgrpChs = settings.channelSelection
                                    .filter((chgrp: any) => (chgrp || []).length >= 2)
                                    .flat()
                                settings.channelSelection = settings.channelSelection.filter((chgrp: any) => {
                                    // check if is a subset of any >2-element chgrp and remove if so.
                                    if ((chgrp || []).length === 1 && lenGte2ChgrpChs.includes(chgrp[0])) {
                                        return false // remove
                                    }
                                    return true // keep
                                })
                                persistent.set(k, settings)
                            })
                    } catch (e) {}
                })()

                // tell Windows electron app user to redefine talkback key
                if (lastOpenedVersionStripped && semver.lte(lastOpenedVersionStripped, '2.2.3')) {
                    if (env.IS_ELECTRON && env.OS === 'win32') {
                        appDispatch({
                            type: 'new_message_dialog',
                            value: {
                                id: 'v2.2.4_set_talkback_key_again_msg',
                                text:
                                    'There has been an update in how the talkback key is handled in this version.\n' +
                                    'If you encounter issues with the talkback key, please try setting it again.',
                                negativeButtonDoNotShowAgain: true,
                            },
                        })
                    }
                }
            }
            persistent.set('lastOpenedVersion', env.APP_VERSION)

            getAudioEngineInstance().setSettings(next.audioEngineDevSettings, authToken)

            if (changed) {
                appDispatch({
                    type: 'update_app_settings',
                    value: next,
                })
            }
        })()

        // console.log(`Calling rtcSession.initialize()`)
        wsConnection.initialize({
            wsHost: Constants.wss_base_path,
            userUrn: user._id,
            authToken,
            appSerialId,
            sessionId,
        })
        logger.setAuthToken(authToken)

        wsConnection.removeEventListener('onmessage', handleWebsocketMessages)
        wsConnection.addEventListener('onmessage', handleWebsocketMessages)

        // set isContentProtectionEnabled
        ElectronIPC.setWindowOptions({ isContentProtectionEnabled: !appState.appSettings.showWindowsDuringScreenshare })

        // set iohook keys
        const talkbackShortcutKeyInfo = persistent.getObject(Constants.keyboardshortcut_talkback) as {
            iohook: number | null
            browser: string | null
        }
        if (talkbackShortcutKeyInfo && talkbackShortcutKeyInfo.iohook) {
            // For now, the only keys we care about is the talkback key, so we send in
            // the array with the talkback key code in it.
            ElectronIPC.iohookSetActiveKeys([talkbackShortcutKeyInfo.iohook])
        } else {
            const backquoteKeyRawCode = env.OS === 'darwin' ? 50 : 192
            persistent.set(Constants.keyboardshortcut_talkback, { browser: 'Backquote', iohook: backquoteKeyRawCode })
            ElectronIPC.iohookSetActiveKeys([backquoteKeyRawCode])
        }

        console.log(`setting is_first_time_app_render true.`)
        persistent.set(Constants.is_first_time_app_render, true)
    }, [])

    /**
     * On component first load (hence, the app first-load) or userUrn changes (logged in),
     * call the `/users/_me` route to get user information.
     *
     * It is guaranteed that localStorage authToken is correctly set when userUrn changes due
     * to a login.
     */
    useNonIdempotentEffect(
        function loadUserOnUserUrnUpdated() {
            if (!state.userUrn) {
                return
            }

            const authToken = getAuthTokenFromLocalUserCache()
            if (!authToken) {
                log.assert(
                    'loadUserOnUserUrnUpdated',
                    'invariant broken! expected ' + `authToken to exist. userUrn='${state.userUrn}'.`
                )
                return
            }

            Api.init(Constants.api_base_path, authToken)

            Api.getUserSelf()
                .then((user) => {
                    set_error(null)
                    loadUser(user, authToken)
                    log.checkpoint('user_loaded', '')
                })
                .catch((e) => {
                    if (e?.status === 404) {
                        // we handle the logout case here. Previously, we had a scheme where a
                        // <Logout> component was rendered which logged the user out, but that
                        // caused an issue where right after the user logged in, a "residue"
                        // <Logout> component still remaining would log out the user right back out.
                        RootSnackbar.show({
                            message: 'The user does not exist (404)',
                            severity: 'info',
                        })
                        logout()
                    } else if (e?.status === 400) {
                        logout()
                    } else {
                        log.error('loadUserOnUserUrnUpdated', `error=` + JSON.stringify(serializeError(e)))
                        set_error(e)
                    }
                })
        },
        [state.userUrn, state.isGuest]
    )

    const [featuresReady, setFeaturesReady] = useState<boolean>(canaryFeatures.ready)
    useEffect(() => {
        const h = () => setFeaturesReady(true)
        canaryFeatures.on('ready', h)
        return () => {
            canaryFeatures.off('ready', h)
        }
    }, [])

    if (env.TRIGGER_LOGIN_ERROR) {
        return <LoginError error={{ status: 400, data: 'This is a test error message.' }} />
    }

    const isGuestJoinPage = window.location.pathname.includes('/guest-join')
    // If there is no cached user, then show the UnauthenticatedApp.
    if (!state.userUrn && !isGuestJoinPage) {
        return <UnauthenticatedApp />
    }

    // Regular user, no features in cache, wait before showing AuthenticatedApp
    if (!featuresReady && !isGuestJoinPage) {
        return (
            <div className="h-full flex justify-center items-center">
                <Loader type="TailSpin" color="#f9cc04" />
            </div>
        )
    }

    if (error) {
        return <ErrorRouter error={error} />
    } else {
        return <AuthenticatedApp />
    }
}

function ErrorRouter({ error }: { error: GetDataError<any> }) {
    const { maintenaceMessage } = useMaintenanceMessageService()

    if (maintenaceMessage) {
        return <MaintenanceError message={maintenaceMessage} />
    }

    if (error.status) {
        return <LoginError error={error} />
    } else {
        // Network error
        return <InternetConnectionError error={error} />
    }
}

export default App
